// Job tray guide — the async job widget opened from the bottom-right launcher
// (Job Queue / History tabs, filters, approve/decline/dismiss/recall) and the
// Global Config "Async Widget" toggle (async-widget/*, JobWidgetHost, ChatPanel
// launcher). Locale-keyed like every guide (content.<lang> → { feature, title, sections }).

const jobTrayGuide = {
  id: "job-tray",
  icon: "cloud_queue",
  keywords: [
    "job",
    "jobs",
    "iş",
    "işler",
    "job queue",
    "iş kuyruğu",
    "queue",
    "kuyruk",
    "widget",
    "async widget",
    "tray",
    "tepsi",
    "activity",
    "etkinlik",
    "history",
    "geçmiş",
    "approve",
    "onayla",
    "decline",
    "reddet",
    "dismiss",
    "kaldır",
    "recall",
    "yeniden ekle",
    "waiting approval",
    "onay bekliyor",
    "processing",
    "işleniyor",
    "failed",
    "başarısız",
    "background",
    "arka plan",
    "launcher",
    "başlatıcı",
    "trabajos",
    "cola de trabajos",
    "bandeja",
    "actividad",
    "historial",
    "aprobar",
    "rechazar",
    "descartar",
    "recuperar",
    "pendiente de aprobación",
    "segundo plano",
  ],
  content: {
    en: {
      feature: "AI",
      title: "Job Queue (Widget)",
      sections: [
        {
          id: "opening",
          title: "Opening the job tray",
          videoUrl: null,
          text:
            "Longer AI work — image generation and editing, image animation, avatars, video, narration, infographics, question batches and similar — runs in the background as **jobs**, so you can keep editing. The job tray (titled **Activity**) shows where each job is and lets you decide what goes onto your scenes.\n\n" +
            "- Click the round purple launcher button in the bottom-right corner of the editor. When the **Chatbot** toggle in **Global Config** is off, it opens the tray directly; when it is on, a small menu offers **Chatbot** and **Widget** — choose **Widget**.\n" +
            "- The tray depends on the **Async Widget** toggle in **Global Config** (on by default). Turn it off and the tray is no longer offered.\n" +
            "- The tray follows the editor's language. Its header also has a language menu (**Türkçe**, **English**, **Español**) if you want the tray in a different language. Click the close icon or anywhere outside the tray to close it.",
        },
        {
          id: "job-queue",
          title: "The Job Queue tab",
          videoUrl: null,
          text:
            "The **Job Queue** tab lists your jobs, newest first, in four groups:\n\n" +
            "- **Waiting Approval** — the result is ready and waits for you to approve or decline it.\n" +
            "- **Finished** — completed narration and voice jobs; click the card to listen.\n" +
            "- **Processing** — still running.\n" +
            "- **Failed** — something went wrong; click the card to see the error.\n\n" +
            "Use the filter at the top right of the tabs to show **All**, **Image**, **Avatar**, **Video** or **Other** jobs. Jobs older than 24 hours move to the **History** tab automatically.",
        },
        {
          id: "job-actions",
          title: "Approving and declining",
          videoUrl: null,
          text:
            "Click a card to open a larger preview. For some jobs — for example an image generation with several candidates — you pick the result you want in that preview before approving.\n\n" +
            "- **Approve** — puts the result onto your training, usually on the scene the job was started from (a new image or video, an updated image, a new infographic scene, generated questions…).\n" +
            "- **Decline** — rejects the result; the job moves to **History** as declined.\n" +
            "- **Dismiss** — removes the card from the list without applying anything.\n" +
            "- **Send to History** — on **Finished** narration jobs, moves the card to **History** once you're done with it.\n\n" +
            "If you already added the result from the window that started the job, its card moves to **History** by itself — no need to approve it again. Some jobs (aspect ratio conversion, subtitle generation, video export) cannot be declined.",
        },
        {
          id: "history",
          title: "The History tab",
          videoUrl: null,
          text:
            "The **History** tab lists approved, declined and older jobs, with the total count at the top. The same filter applies here.\n\n" +
            "- **Recall** — applies a past result to your training again, for example if you deleted it by mistake.\n" +
            "- Text-to-speech (voice), aspect ratio conversion, subtitle and video export jobs cannot be recalled.",
        },
      ],
    },
    tr: {
      feature: "Yapay Zeka",
      title: "İş Kuyruğu (Widget)",
      sections: [
        {
          id: "opening",
          title: "İş tepsisini açma",
          videoUrl: null,
          text:
            "Uzun süren yapay zeka işleri — görsel oluşturma ve düzenleme, görsel canlandırma, avatarlar, video, seslendirme, infografikler, toplu soru üretimi ve benzerleri — arka planda **iş** olarak çalışır; siz de düzenlemeye devam edebilirsiniz. İş tepsisi (başlığı **Etkinlik**) her işin ne durumda olduğunu gösterir ve sahnelerinize neyin ekleneceğine karar vermenizi sağlar.\n\n" +
            "- Editörün sağ alt köşesindeki yuvarlak mor başlatıcı düğmeye tıklayın. **Genel Konfigürasyon** içindeki **Sohbet Botu** anahtarı kapalıysa tepsi doğrudan açılır; açıksa küçük bir menü **Chatbot** ve **Widget** seçeneklerini sunar — **Widget**'ı seçin.\n" +
            "- Tepsi, **Genel Konfigürasyon** içindeki **İş Kuyruğu** anahtarına bağlıdır (varsayılan olarak açık). Kapatırsanız tepsi artık sunulmaz.\n" +
            "- Tepsi editörün dilini izler. Tepsiyi farklı bir dilde görmek isterseniz başlığındaki dil menüsünden (**Türkçe**, **English**, **Español**) seçebilirsiniz. Kapatmak için kapat simgesine ya da tepsinin dışında herhangi bir yere tıklayın.",
        },
        {
          id: "job-queue",
          title: "İş Kuyruğu sekmesi",
          videoUrl: null,
          text:
            "**İş Kuyruğu** sekmesi işlerinizi en yeniden eskiye doğru dört grupta listeler:\n\n" +
            "- **Onay Bekliyor** — sonuç hazırdır ve onaylamanızı ya da reddetmenizi bekler.\n" +
            "- **Bitmiş İşler** — tamamlanan seslendirme işleri; dinlemek için karta tıklayın.\n" +
            "- **İşleniyor** — hâlâ çalışan işler.\n" +
            "- **Başarısız** — bir sorun oluşmuştur; hatayı görmek için karta tıklayın.\n\n" +
            "Sekmelerin sağ üstündeki filtreyle **Tümü**, **Görsel**, **Avatar**, **Video** veya **Diğer** işleri gösterebilirsiniz. 24 saatten eski işler otomatik olarak **Geçmiş** sekmesine taşınır.",
        },
        {
          id: "job-actions",
          title: "Onaylama ve reddetme",
          videoUrl: null,
          text:
            "Daha büyük bir önizleme açmak için bir karta tıklayın. Bazı işlerde — örneğin birden fazla aday üreten bir görsel oluşturmada — onaylamadan önce istediğiniz sonucu bu önizlemede seçersiniz.\n\n" +
            "- **Onayla** — sonucu eğitiminize ekler; genellikle işin başlatıldığı sahneye (yeni bir görsel veya video, güncellenmiş bir görsel, yeni bir infografik sahne, üretilen sorular…).\n" +
            "- **Reddet** — sonucu reddeder; iş reddedilmiş olarak **Geçmiş**'e taşınır.\n" +
            "- **Kaldır** — hiçbir şey uygulamadan kartı listeden kaldırır.\n" +
            "- **Geçmişe Yolla** — **Bitmiş İşler** grubundaki seslendirme işlerinde, işiniz bittiğinde kartı **Geçmiş**'e taşır.\n\n" +
            "Sonucu işi başlatan pencereden zaten eklediyseniz kart kendiliğinden **Geçmiş**'e geçer — yeniden onaylamanız gerekmez. Bazı işler (en-boy oranı dönüşümü, altyazı oluşturma, video dışa aktarma) reddedilemez.",
        },
        {
          id: "history",
          title: "Geçmiş sekmesi",
          videoUrl: null,
          text:
            "**Geçmiş** sekmesi onaylanmış, reddedilmiş ve eski işleri, üstte toplam sayıyla birlikte listeler. Aynı filtre burada da geçerlidir.\n\n" +
            "- **Yeniden Ekle** — geçmişteki bir sonucu eğitiminize yeniden uygular; örneğin yanlışlıkla sildiyseniz.\n" +
            "- Metin seslendirme, en-boy oranı dönüşümü, altyazı ve video dışa aktarma işleri yeniden eklenemez.",
        },
      ],
    },
    es: {
      feature: "IA",
      title: "Cola de trabajos (Widget)",
      sections: [
        {
          id: "opening",
          title: "Abrir la bandeja de trabajos",
          videoUrl: null,
          text:
            "Las tareas de IA más largas (generación y edición de imágenes, animación de imágenes, avatares, vídeo, narración, infografías, lotes de preguntas y similares) se ejecutan en segundo plano como **trabajos**, para que puedas seguir editando. La bandeja de trabajos (titulada **Actividad**) muestra en qué punto está cada trabajo y te permite decidir qué se añade a tus escenas.\n\n" +
            "- Haz clic en el botón redondo morado de la esquina inferior derecha del editor. Si el interruptor **Chatbot** de **Configuración Global** está desactivado, se abre directamente la bandeja; si está activado, un pequeño menú ofrece **Chatbot** y **Widget**: elige **Widget**.\n" +
            "- La bandeja depende del interruptor **Async Widget** de **Configuración Global** (activado por defecto). Si lo desactivas, la bandeja deja de estar disponible.\n" +
            "- La bandeja sigue el idioma del editor. Su encabezado también tiene un menú de idioma (**Türkçe**, **English**, **Español**) por si quieres verla en otro idioma. Para cerrarla, haz clic en el icono de cerrar o en cualquier lugar fuera de la bandeja.",
        },
        {
          id: "job-queue",
          title: "La pestaña Cola de trabajos",
          videoUrl: null,
          text:
            "La pestaña **Cola de trabajos** muestra tus trabajos, del más reciente al más antiguo, en cuatro grupos:\n\n" +
            "- **Pendiente de aprobación**: el resultado está listo y espera a que lo apruebes o lo rechaces.\n" +
            "- **Finalizado**: trabajos de narración y voz completados; haz clic en la tarjeta para escucharlos.\n" +
            "- **Procesando**: siguen ejecutándose.\n" +
            "- **Error**: algo ha salido mal; haz clic en la tarjeta para ver el error.\n\n" +
            "Usa el filtro de la parte superior derecha de las pestañas para mostrar los trabajos **Todos**, **Imagen**, **Avatar**, **Vídeo** u **Otros**. Los trabajos de más de 24 horas pasan automáticamente a la pestaña **Historial**.",
        },
        {
          id: "job-actions",
          title: "Aprobar y rechazar",
          videoUrl: null,
          text:
            "Haz clic en una tarjeta para abrir una vista previa más grande. En algunos trabajos (por ejemplo, una generación de imágenes con varias candidatas) eliges en esa vista previa el resultado que quieres antes de aprobarlo.\n\n" +
            "- **Aprobar**: añade el resultado a tu formación, normalmente en la escena desde la que se inició el trabajo (una imagen o un vídeo nuevos, una imagen actualizada, una escena infográfica nueva, preguntas generadas…).\n" +
            "- **Rechazar**: rechaza el resultado; el trabajo pasa a **Historial** como rechazado.\n" +
            "- **Descartar**: quita la tarjeta de la lista sin aplicar nada.\n" +
            "- **Enviar al historial**: en los trabajos de narración del grupo **Finalizado**, mueve la tarjeta a **Historial** cuando ya no la necesitas.\n\n" +
            "Si ya añadiste el resultado desde la ventana que inició el trabajo, su tarjeta pasa sola a **Historial**; no hace falta aprobarla otra vez. Algunos trabajos (conversión de relación de aspecto, generación de subtítulos, exportación de vídeo) no se pueden rechazar.",
        },
        {
          id: "history",
          title: "La pestaña Historial",
          videoUrl: null,
          text:
            "La pestaña **Historial** muestra los trabajos aprobados, rechazados y antiguos, con el total arriba. Aquí se aplica el mismo filtro.\n\n" +
            "- **Recuperar**: vuelve a aplicar un resultado anterior a tu formación, por ejemplo si lo eliminaste por error.\n" +
            "- Los trabajos de texto a voz, conversión de relación de aspecto, subtítulos y exportación de vídeo no se pueden recuperar.",
        },
      ],
    },
  },
};

export default jobTrayGuide;
